import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import './ProfilePage.css';
import ditherArizona from '../assets/images/dither-arizona.png';

const ProfilePage = () => {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [allowPromo, setAllowPromo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [editingField, setEditingField] = useState(null);
  const [userDesigns, setUserDesigns] = useState([]);
  const [designsLoading, setDesignsLoading] = useState(true);

  useEffect(() => {
    if (currentUser) {
      const fetchUserData = async () => {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const data = userDocSnap.data();
          setUserData(data);
          setDisplayName(data.displayName || '');
          setPhoneNumber(data.phoneNumber || '');
          setZipCode(data.zipCode || '');
          setAllowPromo(data.allowPromo || false);
        }
        setLoading(false);
      };
      fetchUserData();
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      const fetchUserDesigns = async () => {
        try {
                  const designsRef = collection(db, 'designs');
        const q = query(
          designsRef, 
          where('userId', '==', currentUser.uid),
          orderBy('createdAt', 'desc')
        );
        const querySnapshot = await getDocs(q);
        const designs = [];
        querySnapshot.forEach((doc) => {
          designs.push({
            id: doc.id,
            ...doc.data()
          });
        });
        setUserDesigns(designs);
        } catch (error) {
          console.error("Error fetching user designs: ", error);
        } finally {
          setDesignsLoading(false);
        }
      };

      fetchUserDesigns();
    }
  }, [currentUser]);

  // Sanitize display name
  const sanitizeDisplayName = (name) => {
    return name
      .trim()
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .replace(/[<>]/g, '') // Remove potential HTML tags
      .substring(0, 50); // Limit to 50 characters
  };

  // Sanitize and format phone number
  const sanitizePhoneNumber = (phone) => {
    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, '');
    
    // Format US phone numbers (assuming US format)
    if (digitsOnly.length === 10) {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
    } else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      return `+1 (${digitsOnly.slice(1, 4)}) ${digitsOnly.slice(4, 7)}-${digitsOnly.slice(7)}`;
    }
    
    // Return original if not a standard US format
    return phone.trim();
  };

  // Sanitize and validate zip code
  const sanitizeZipCode = (zip) => {
    // Remove all non-digit characters
    const digitsOnly = zip.replace(/\D/g, '');
    
    // Format US zip codes
    if (digitsOnly.length === 5) {
      return digitsOnly;
    } else if (digitsOnly.length === 9) {
      return `${digitsOnly.slice(0, 5)}-${digitsOnly.slice(5)}`;
    }
    
    // Return original if not a standard US format
    return zip.trim();
  };

  // Validate display name
  const validateDisplayName = (name) => {
    const sanitized = sanitizeDisplayName(name);
    if (sanitized.length < 2) {
      return { isValid: false, message: 'Display name must be at least 2 characters long.' };
    }
    if (sanitized.length > 50) {
      return { isValid: false, message: 'Display name must be 50 characters or less.' };
    }
    return { isValid: true, sanitized };
  };

  // Validate phone number
  const validatePhoneNumber = (phone) => {
    const digitsOnly = phone.replace(/\D/g, '');
    if (phone.trim() === '') {
      return { isValid: true, sanitized: '' }; // Empty is valid
    }
    if (digitsOnly.length < 10) {
      return { isValid: false, message: 'Phone number must have at least 10 digits.' };
    }
    if (digitsOnly.length > 15) {
      return { isValid: false, message: 'Phone number is too long.' };
    }
    return { isValid: true, sanitized: sanitizePhoneNumber(phone) };
  };

  // Validate zip code
  const validateZipCode = (zip) => {
    const digitsOnly = zip.replace(/\D/g, '');
    if (zip.trim() === '') {
      return { isValid: true, sanitized: '' }; // Empty is valid
    }
    if (digitsOnly.length < 5) {
      return { isValid: false, message: 'Zip code must have at least 5 digits.' };
    }
    if (digitsOnly.length > 10) {
      return { isValid: false, message: 'Zip code is too long.' };
    }
    return { isValid: true, sanitized: sanitizeZipCode(zip) };
  };

  const handleSaveField = async (field) => {
    if (!editingField) return;
    
    let validation;
    let sanitizedValue = '';
    
    if (field === 'displayName') {
      validation = validateDisplayName(displayName);
      if (!validation.isValid) {
        setMessage(validation.message);
        return;
      }
      sanitizedValue = validation.sanitized;
    } else if (field === 'phoneNumber') {
      validation = validatePhoneNumber(phoneNumber);
      if (!validation.isValid) {
        setMessage(validation.message);
        return;
      }
      sanitizedValue = validation.sanitized;
    } else if (field === 'zipCode') {
      validation = validateZipCode(zipCode);
      if (!validation.isValid) {
        setMessage(validation.message);
        return;
      }
      sanitizedValue = validation.sanitized;
    }
    
    const userDocRef = doc(db, 'users', currentUser.uid);
    try {
      const updateData = {};
      if (field === 'displayName') {
        updateData.displayName = sanitizedValue;
        setDisplayName(sanitizedValue);
      } else if (field === 'phoneNumber') {
        updateData.phoneNumber = sanitizedValue;
        setPhoneNumber(sanitizedValue);
      } else if (field === 'zipCode') {
        updateData.zipCode = sanitizedValue;
        setZipCode(sanitizedValue);
      }
      
      await updateDoc(userDocRef, updateData);
      setUserData(prev => ({ ...prev, ...updateData }));
      setMessage(`${field === 'displayName' ? 'Display name' : field === 'phoneNumber' ? 'Phone number' : 'Zip code'} updated successfully!`);
      setEditingField(null);
    } catch (error) {
      console.error("Error updating profile: ", error);
      setMessage('Failed to update profile.');
    }
  };

  const handleCancelEdit = () => {
    setDisplayName(userData?.displayName || '');
    setPhoneNumber(userData?.phoneNumber || '');
    setZipCode(userData?.zipCode || '');
    setEditingField(null);
    setMessage('');
  };

  const handleTogglePromo = async () => {
    const newAllowPromo = !allowPromo;
    setAllowPromo(newAllowPromo);
    
    const userDocRef = doc(db, 'users', currentUser.uid);
    try {
      await updateDoc(userDocRef, { allowPromo: newAllowPromo });
      setUserData(prev => ({ ...prev, allowPromo: newAllowPromo }));
      setMessage(newAllowPromo ? 'Promotional communications enabled!' : 'Promotional communications disabled.');
    } catch (error) {
      console.error("Error updating promotional settings: ", error);
      setAllowPromo(!newAllowPromo); // Revert on error
      setMessage('Failed to update promotional settings.');
    }
  };

  const startEditing = (field) => {
    setEditingField(field);
    setMessage('');
  };

  const handleKeyPress = (e, field) => {
    if (e.key === 'Enter') {
      handleSaveField(field);
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleCreateDesign = () => {
    // Navigate to design creation page
    window.location.href = '/design';
  };

  const handleEditDesign = (designId) => {
    // TODO: Navigate to design editor
    console.log('Edit design:', designId);
  };

  const handleDeleteDesign = async (designId) => {
    if (window.confirm('Are you sure you want to delete this design?')) {
      try {
        // TODO: Implement design deletion
        console.log('Delete design:', designId);
        setMessage('Design deleted successfully!');
      } catch (error) {
        console.error("Error deleting design: ", error);
        setMessage('Failed to delete design.');
      }
    }
  };

  const handleViewDesign = (designId) => {
    // TODO: Navigate to design view page
    console.log('View design:', designId);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="profile-container">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Welcome, {userData?.displayName ? (
          <Link to={`/gallery/${currentUser.uid}`} className="profile-display-name-link">
            {userData.displayName}
          </Link>
        ) : 'User'}</p>
        <br></br>
        <button 
          className="update-profile-btn"
          onClick={() => {
            document.querySelector('.profile-details').scrollIntoView({ 
              behavior: 'smooth',
              block: 'start'
            });
          }}
        >
          Update Profile
        </button>
      </div>
      
      {message && <p className="update-message">{message}</p>}
      
      {/* Your Designs Section - Moved to top */}
      <div className="designs-section">
        <h3>Your Designs</h3>
        {designsLoading ? (
          <div className="empty-designs">
            <div className="empty-designs-text">Loading designs...</div>
          </div>
        ) : (
          <div className="designs-grid">
            {/* Create New Design Card - Always First */}
            <div 
              className="design-card create-design-card"
              onClick={handleCreateDesign}
              style={{ backgroundImage: `url(${ditherArizona})` }}
            >
              <div className="create-design-content">
                <div className="create-design-text">Create New Design</div>
              </div>
            </div>

            {/* User Designs */}
            {userDesigns.map((design) => (
              <Link to={`/gallery/${design.id}`} key={design.id} className="consistent-design-card">
                <div className="design-image">
                  <img 
                    src={design.previewUrl || design.imageUrl || design.patternUrl || ditherArizona} 
                    alt={design.name || 'Design'} 
                  />
                </div>
                <div className="design-info">
                  <h4 className="design-name">{design.name || 'No Name'}</h4>
                  <p className="design-date">
                    {design.createdAt?.toDate().toLocaleDateString() || 'Unknown date'}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <br></br>
      
      {/* Account Information Section */}
      <div className="profile-details">
        <h3>Account Information</h3>
        
        <div className="profile-details-content">
          <div className="profile-field">
            <label>Display Name</label>
            {editingField === 'displayName' ? (
              <div className="edit-field">
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  onKeyDown={(e) => handleKeyPress(e, 'displayName')}
                  placeholder="Enter your display name"
                  maxLength="50"
                  autoFocus
                />
                <div className="edit-actions">
                  <button 
                    className="save-btn"
                    onClick={() => handleSaveField('displayName')}
                  >
                    Save
                  </button>
                  <button 
                    className="cancel-btn"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div 
                className="editable-field"
                onClick={() => startEditing('displayName')}
              >
                <span>{userData?.displayName || 'Click to add display name'}</span>
                <span className="edit-icon">✏️</span>
              </div>
            )}
          </div>

          <div className="profile-field">
            <label>Email</label>
            <div className="static-field">
              {currentUser.email}
            </div>
          </div>

          <div className="profile-field">
            <label>Date Joined</label>
            <div className="static-field">
              {userData?.createdAt?.toDate().toLocaleDateString() || 'N/A'}
            </div>
          </div>

          <div className="profile-field">
            <label>Zip Code</label>
            {editingField === 'zipCode' ? (
              <div className="edit-field">
                <input
                  type="text"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  onKeyDown={(e) => handleKeyPress(e, 'zipCode')}
                  placeholder="Please enter home zip code"
                  autoFocus
                />
                <div className="edit-actions">
                  <button 
                    className="save-btn"
                    onClick={() => handleSaveField('zipCode')}
                  >
                    Save
                  </button>
                  <button 
                    className="cancel-btn"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div 
                className="editable-field"
                onClick={() => startEditing('zipCode')}
              >
                <span>{userData?.zipCode || 'Please enter home zip code'}</span>
                <span className="edit-icon">✏️</span>
              </div>
            )}
          </div>

          <div className="profile-field">
            <label>Phone Number</label>
            {editingField === 'phoneNumber' ? (
              <div className="edit-field">
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  onKeyDown={(e) => handleKeyPress(e, 'phoneNumber')}
                  placeholder="Enter your phone number"
                  autoFocus
                />
                <div className="edit-actions">
                  <button 
                    className="save-btn"
                    onClick={() => handleSaveField('phoneNumber')}
                  >
                    Save
                  </button>
                  <button 
                    className="cancel-btn"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div 
                className="editable-field"
                onClick={() => startEditing('phoneNumber')}
              >
                <span>{userData?.phoneNumber || 'Click to add phone number'}</span>
                <span className="edit-icon">✏️</span>
              </div>
            )}
          </div>
          
          <div className="communication-preference">
            <div className="preference-info">
              <h4>Promotional Communications</h4>
              <p>Receive updates about new products, sales, and exclusive offers</p>
            </div>
            <div className="toggle-container">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={allowPromo}
                  onChange={handleTogglePromo}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Design Button */}
      <div className="bottom-design-section">
        <button 
          className="bottom-design-btn"
          onClick={handleCreateDesign}
        >
          Design
        </button>
      </div>
    </div>
  );
};

export default ProfilePage; 